import { useEffect, useState } from 'react'
import { OverseasCompanyData } from '../../../../../types/api'
import { EyecatchBg } from '../domestic/CompanySummary'
import { Shareholders } from './Shareholders'
import { css } from '@emotion/react'
import { ValuationInfo } from './ValuationInfo'
import { colors, CompanyLogo, Divider, font, mq, Tag } from 'plume-ui'
import { companyDefaultImage } from '@/lib/company'
import { RoundType } from './RoundType'
import { EmployeesNumber } from './EmployeesNumber'
import { Description } from '../Description'
import { TagButton, TagButtonGroup } from '../../../../atoms/TagButton'
import { IpoValuationInfo } from './IpoValuationInfo'
import { checkSummaryStickyPosition } from '@/lib/sticky'

type Props = {
  company: OverseasCompanyData
}

export const OverseasCompanySummary: React.VFC<Props> = (props) => {
  return (
    <div
      css={css`
        display: inline;
      `}
    >
      <OverseasCompanyHero company={props.company} />
      <div
        css={css`
          display: flex;
          flex-direction: column;
          max-width: 1200px;
          margin: 0 auto;
        `}
      >
        <div
          css={css`
            ${mq.untilMedium} {
              padding: 50px 20px 10px;
            }
            ${mq.large} {
              margin-left: 212px;
              margin-bottom: 24px;
              min-height: 64px;
            }
          `}
        >
          <Description company={props.company} />
          <div
            css={css`
              margin-top: 10px;
            `}
          >
            {props.company.tags.length > 0 ? (
              <TagButtonGroup className="tag-list">
                {props.company.tags.map((tag, i) => (
                  <TagButton key={i} tag={tag} />
                ))}
              </TagButtonGroup>
            ) : null}
          </div>
        </div>
        <Divider
          dashed
          css={css`
            width: 90%;
            margin: 5px auto;
            ${mq.large} {
              display: none;
            }
          `}
        />
        <TopInfo company={props.company} />
      </div>
    </div>
  )
}

const OverseasCompanyHero: React.VFC<{
  company: OverseasCompanyData
}> = (props) => {
  const [isCompanySummaryStickyPosition, setIsCompanySummaryStickyPosition] =
    useState(false)

  useEffect(() => {
    window.addEventListener('scroll', () => {
      setIsCompanySummaryStickyPosition(checkSummaryStickyPosition)
    })
    return () => {
      window.removeEventListener('scroll', () => {
        setIsCompanySummaryStickyPosition(checkSummaryStickyPosition)
      })
    }
  }, [])

  return (
    <EyecatchBg
      tags={[]}
      sticky={isCompanySummaryStickyPosition}
      css={css`
        ${mq.large} {
          margin-block-start: 24px;
        }
      `}
    >
      <div
        css={css`
          display: flex;
          align-items: flex-end;
          width: 100%;
          max-width: ${isCompanySummaryStickyPosition && '1120px'};
          margin-block-start: auto;
          margin-inline: auto;
          padding-inline: ${isCompanySummaryStickyPosition ? '0' : '80px'};
          color: ${colors.white00};
        `}
      >
        <div
          css={css`
            display: flex;
            align-items: ${isCompanySummaryStickyPosition ? 'center' : 'end'};
            column-gap: ${isCompanySummaryStickyPosition ? '16px' : '32px'};
            min-width: 0;
            min-height: ${isCompanySummaryStickyPosition ? '96px' : '80px'};
          `}
        >
          <div
            css={css`
              flex-grow: 0;
              flex-shrink: 0;
              margin-block-end: ${isCompanySummaryStickyPosition
                ? '0'
                : '-80px'};
            `}
          >
            <CompanyLogo
              className="logo"
              name={props.company.name}
              src={props.company.logoUrl || companyDefaultImage('未公開企業')}
              size={isCompanySummaryStickyPosition ? 'normal' : 'extra-large'}
              css={css`
                ${mq.untilMedium} {
                  width: 96px !important;
                  height: 96px !important;
                }
              `}
            />
          </div>
          <CompanyName
            isIpo={props.company.listed}
            isCompanySummaryStickyPosition={isCompanySummaryStickyPosition}
          >
            {props.company.name}
          </CompanyName>
        </div>
      </div>
    </EyecatchBg>
  )
}

type CompanyNameProps = {
  isIpo: boolean
  isCompanySummaryStickyPosition: boolean
  children: React.ReactNode
}

const CompanyName: React.FC<CompanyNameProps> = (props) => {
  return (
    <>
      <div
        css={css`
          display: none;
          flex-grow: 1;
          min-width: 0;
          ${mq.large} {
            display: flex;
            flex-direction: column;
            row-gap: 2px;
            padding-block-end: ${props.isCompanySummaryStickyPosition
              ? '0'
              : '8px'};
          }
        `}
      >
        {props.isIpo && (
          <p
            css={css`
              margin: 0;
            `}
          >
            <Tag
              className="ipo-label"
              inverted
              square
              color="red"
              css={css`
                font-size: 12px;
              `}
            >
              IPO
            </Tag>
          </p>
        )}
        <h1
          css={css`
            margin: 0;
            font-size: 20px;
            color: ${colors.white00};
            font-size: ${props.isCompanySummaryStickyPosition
              ? '28px'
              : '32px'};
            text-decoration: none;
            font-weight: ${font.weight.bold};
            line-height: 1.25;
            letter-spacing: 0.01em;
            overflow-wrap: break-word;
            transition: font-size 0.16s 0s linear;
            ${props.isCompanySummaryStickyPosition &&
            'text-overflow: ellipsis; overflow: hidden; white-space: nowrap;'}
          `}
        >
          {props.children}
        </h1>
      </div>
    </>
  )
}

const TopInfo: React.VFC<{ company: OverseasCompanyData }> = (props) => {
  return (
    <div
      css={css`
        display: flex;
        flex-direction: column;
        ${mq.large} {
          padding-left: 20px;
          flex-direction: row;
          min-height: 82px;
          margin-bottom: 28px;
        }
      `}
    >
      <div
        css={css`
          width: 160px;
          min-width: 160px;
          margin-right: 32px;
        `}
      >
        <RoundType className="last-funding-type" company={props.company} />
      </div>
      <div
        css={css`
          min-width: 176px;
          margin-right: 16px;
        `}
        data-test-id="overview-valuation"
      >
        {props.company.listed ? (
          <IpoValuationInfo ipoData={props.company.ipo} />
        ) : (
          <ValuationInfo valuation={props.company.valuation} />
        )}
      </div>
      <div
        css={css`
          min-width: 176px;
          margin-right: 16px;
        `}
      >
        <EmployeesNumber
          className="employees_num"
          start={props.company.employee.range.start}
          end={props.company.employee.range.end}
        />
      </div>
      <div>
        <Shareholders shareholders={props.company.shareholders} />
      </div>
    </div>
  )
}

type SummaryLabelProps = {
  children: React.ReactNode
}

export const SummaryLabel: React.FC<SummaryLabelProps> = (props) => {
  return (
    <div
      css={css`
        color: ${colors.black10};
        font-style: normal;
        font-weight: ${font.weight.bold};
        height: 18px;
        font-size: 13px;
        display: flex;
        text-align: center;
        align-items: center;
      `}
    >
      {props.children}
    </div>
  )
}
